/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    NodeTag,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { createAccordionStateInput } from '@msdyn365-commerce-modules/accordion';
import { IDobbiesAccordionData } from './dobbies-accordion.data';
import { IDobbiesAccordionProps } from './dobbies-accordion.props.autogenerated';

/**
 *
 * Accordion component.
 * @extends {React.PureComponent<IDobbiesAccordionProps<IDobbiesAccordionData>>}
 */

export interface ICallToActionProps {
    link: INodeProps[];
}

/**
 * Added interface for state management.
 */
export interface IAccordianState {
    isDisabled: boolean;
}

export interface IAccordionViewProps extends IDobbiesAccordionProps<{}> {
    heading?: React.ReactNode;
    AccordionContainer: IModuleProps;
    HeaderSection: INodeProps;
    HeadingWrapper: INodeProps;
    accordianButtonsContainer: INodeProps;
    accordionButtons: React.ReactNode;
    accordionItemContainer: INodeProps;
    accordionItems: React.ReactNode;
}

/**
 *
 * Accordion component.
 * @extends {React.Component<IDobbiesAccordionProps<IDobbiesAccordionData>>}
 */
@observer
class Accordion extends React.PureComponent<IDobbiesAccordionProps<IDobbiesAccordionData>, IAccordianState> {
    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private readonly expandAllButtonRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    private readonly collapseAllButtonRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    constructor(props: IDobbiesAccordionProps<IDobbiesAccordionData>) {
        super(props);
        this._onExpandAll = this._onExpandAll.bind(this);
        this._onCollapseAll = this._onCollapseAll.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this.state = { isDisabled: false };
    }

    public render(): JSX.Element {
        const { slots } = this.props;
        const { heading, className } = this.props.config;
        const headingComponent = heading && heading.text && (
            <Msdyn365.Text
                className='ms-accordion-header-section__heading'
                tag={heading.tag || 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        const accordionViewProps = {
            ...this.props,
            AccordionContainer: {
                moduleProps: this.props,
                className: classnames('ms-accordion', className)
            },
            HeaderSection: { className: 'ms-accordion-header-section' },
            HeadingWrapper: { className: 'ms-accordion-header-section-container' },
            heading: headingComponent,
            config: this.props && this.props.config,
            accordianButtonsContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-accordion-header-section-toggle-button-container'
            },
            accordionButtons: this._renderAccordionButton(),
            accordionItemContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-accordion-item-section'
            },
            accordionItems:
                slots &&
                slots.accordionItems &&
                slots.accordionItems.length > 0 &&
                slots.accordionItems.map((item: React.ReactNode, index: number) => this._getAccordionItem(item, index))
        };
        return this.props.renderView(accordionViewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private _renderAccordionButton(): React.ReactNode | null {
        const { config, resources } = this.props;
        const { heading } = config;
        this.payLoad.contentAction.etext = TelemetryConstant.ExpandAll;
        const attributeExpandAll = getTelemetryAttributes(this.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.CollapseAll;
        const attributeCollapseAll = getTelemetryAttributes(this.telemetryContent!, this.payLoad);
        if (!config.showToggleAll) {
            this.props.context.telemetry.error('Accordion button content is empty, module wont render');
            return null;
        }
        return (
            <>
                <button
                    disabled={this.state.isDisabled}
                    className='ms-accordion_ExpandAll'
                    aria-label={`${resources.expandAllText} ${heading?.text ? heading.text : ''}`}
                    onClick={this._onExpandAll}
                    ref={this.expandAllButtonRef}
                    {...attributeExpandAll}
                >
                    {resources.expandAllText}
                </button>
                &nbsp;
                {resources.toggleAllSeparator}
                &nbsp;
                <button
                    className='ms-accordion_CollapseAll'
                    aria-label={`${resources.collapseAllText} ${heading?.text ? heading.text : ''}`}
                    onClick={this._onCollapseAll}
                    ref={this.collapseAllButtonRef}
                    {...attributeCollapseAll}
                    disabled={!this.state.isDisabled}
                >
                    {resources.collapseAllText}
                </button>
            </>
        );
    }

    private _onExpandAll(): void {
        const accordionState = { isAllExpanded: true };
        this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
        if (accordionState.isAllExpanded) {
            this.setState({
                isDisabled: true
            });
            setTimeout(() => {
                this.collapseAllButtonRef.current?.focus();
            }, 50);
        }
    }

    private _onCollapseAll(): void {
        const accordionState = { isAllExpanded: false };
        this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
        if (!accordionState.isAllExpanded) {
            this.setState({
                isDisabled: false
            });
            setTimeout(() => {
                this.expandAllButtonRef.current?.focus();
            }, 50);
        }
    }

    private _getAccordionItem(item: React.ReactNode, index: number): React.ReactNode {
        return (
            <React.Fragment key={index}>
                {React.cloneElement(item as React.ReactElement, { interactionStyle: this.props.config.interactionStyle })}
            </React.Fragment>
        );
    }
}

export default Accordion;
